import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../common/Button';

const GuideHome: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNewReading = () => {
    navigate('/new-reading');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto px-6 py-12"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment">
            Tarot Guide
          </h1>
          <p className="mt-3 text-lg text-ink dark:text-parchment/60 max-w-2xl mx-auto">
            This is your super companion and reading guide that helps you learn more about Tarot. Explore spreads, get card meanings, and lean even deeper into your own reading power.
          </p>
          <p className="mt-2 text-lg text-ink dark:text-parchment/60">
            <strong><em>Brand new to Tarot? Check out our <Link to="/guide/101" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">Tarot 101</Link> page for the basics.</em></strong>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-vellum dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 flex flex-col h-full hover:shadow-md hover:-translate-y-px transition-all">
            <h2 className="text-xl font-display font-semibold text-sun dark:text-ember mb-3">
              Tarot Spreads
            </h2>

            <p className="text-ink dark:text-parchment/60 mb-6 flex-grow">
              From simple one-card pulls to the mystical Celtic Cross, discover how different spreads illuminate different aspects of your journey. Learn the sacred geometry of card layouts and how each position weaves its meaning into the greater tapestry.
            </p>

            <Button
              variant="primary"
              onClick={() => navigate('/guide/spreads')}
              fullWidth
            >
              Find Your Spread
            </Button>
          </div>

          <div className="bg-vellum dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 flex flex-col h-full hover:shadow-md hover:-translate-y-px transition-all">
            <h2 className="text-xl font-display font-semibold text-sun dark:text-ember mb-3">
              Card Meanings
            </h2>

            <p className="text-ink dark:text-parchment/60 mb-6 flex-grow">
              Want to search for a specific card and get it's meaning? Each card is a portal to understanding, a mirror reflecting the mysteries of life. Use this in your own readings or to learn more about a specific card.
            </p>

            <Button
              variant="secondary"
              onClick={() => navigate('/guide/cards')}
              fullWidth
            >
              Get Card Meanings
            </Button>
          </div>
        </div>

        <div className="mt-10 bg-vellum dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
          <span className="font-display text-xl font-semibold text-sun dark:text-ember">Suggested Reading Rituals</span>

          <ul className="space-y-2 mt-4 text-ink dark:text-parchment/60">
            <li className="flex items-start">
              <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
              <span>Ground yourself and set sacred space before each reading</span>
            </li>
            <li className="flex items-start">
              <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
              <span>Let your intuition guide you to the spread that calls to you</span>
            </li>
            <li className="flex items-start">
              <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
              <span>Trust the whispers of your inner wisdom as you interpret the cards</span>
            </li>
            <li className="flex items-start">
              <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
              <span>Notice how the cards speak to each other and weave their story</span>
            </li>
            <li className="flex items-start">
              <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
              <span>Reversed cards invite us to look deeper at internal energies</span>
            </li>
            <li className="flex items-start">
              <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
              <span>Keep a journal of your readings to track your magical journey</span>
            </li>
          </ul>
        </div>

        <div className="mt-10 text-center">
          <p className="text-2xl font-display text-ink dark:text-parchment/60 mb-4">
            Ready to pull your own cards?
          </p>
          <Button
            variant="primary"
            onClick={handleNewReading}
          >
            Start a New Reading
          </Button>
        </div>

        <div className="mt-8 text-center space-x-4">
          <Link
            to="/guide/101"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot 101
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/about"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            About Tara
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/faq"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            About This App
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default GuideHome;
